/*
  GEMDRAW — Predictive search recovery states

  The theme's predictive search is left completely intact: its markup, its
  keyboard handling, its live region and its loading and no-results states all
  keep working exactly as shipped.

  What this adds is the two situations the theme has no answer for — the
  visitor being offline, and the search endpoint failing. In both cases the
  panel would otherwise just stay empty, which reads as "nothing matched".
  Instead it explains what happened and what to do.

  Detection is scoped to the predictive search endpoint only. Every other
  request on the page passes through untouched.
*/

(function () {
  'use strict';

  const STATE_CLASS = 'gd-search-state';
  const STATE_ATTR = 'data-gd-search-state';

  function strings() {
    return window.gemdrawStrings || {};
  }

  function panels() {
    return document.querySelectorAll('[data-predictive-search]');
  }

  function clearState() {
    document.querySelectorAll('[' + STATE_ATTR + ']').forEach(function (node) {
      node.remove();
    });

    /* Only give back the panels this script opened. Any panel the theme opened
       on its own is left exactly as the theme left it. */
    document.querySelectorAll('predictive-search[data-gd-forced-open]').forEach(function (host) {
      host.removeAttribute('data-gd-forced-open');
      host.removeAttribute('open');
    });
  }

  /* The results panel is hidden until the host element carries the open
     attribute, so a message appended into a closed panel would never be seen. */
  function reveal(panel) {
    const host = panel.closest('predictive-search');
    if (!host || host.hasAttribute('open')) return;

    host.setAttribute('open', 'true');
    host.setAttribute('data-gd-forced-open', 'true');
  }

  function showState(kind) {
    const copy = strings();
    const title = kind === 'offline' ? copy.searchOfflineTitle : copy.searchErrorTitle;
    const body = kind === 'offline' ? copy.searchOfflineBody : copy.searchErrorBody;
    if (!title) return;

    clearState();

    panels().forEach(function (panel) {
      const state = document.createElement('div');
      state.className = STATE_CLASS;
      state.setAttribute(STATE_ATTR, kind);
      state.setAttribute('role', 'status');

      const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      icon.setAttribute('class', 'gd-icon gd-search-state__icon');
      icon.setAttribute('width', '20');
      icon.setAttribute('height', '20');
      icon.setAttribute('viewBox', '0 0 24 24');
      icon.setAttribute('aria-hidden', 'true');
      icon.setAttribute('focusable', 'false');
      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttribute('href', '#gd-icon-live');
      icon.appendChild(use);

      const text = document.createElement('div');
      const titleEl = document.createElement('span');
      titleEl.className = 'gd-search-state__title';
      titleEl.textContent = title;
      const bodyEl = document.createElement('span');
      bodyEl.className = 'gd-search-state__body';
      bodyEl.textContent = body || '';
      text.appendChild(titleEl);
      text.appendChild(bodyEl);

      state.appendChild(icon);
      state.appendChild(text);
      panel.appendChild(state);
      reveal(panel);
    });
  }

  function isPredictiveSearchRequest(input) {
    const route = window.routes && window.routes.predictive_search_url;
    if (!route) return false;

    const url = typeof input === 'string' ? input : input && input.url;
    return typeof url === 'string' && url.indexOf(route) !== -1;
  }

  const nativeFetch = window.fetch;
  if (typeof nativeFetch !== 'function') return;

  window.fetch = function (input, init) {
    const watched = isPredictiveSearchRequest(input);
    const request = nativeFetch.apply(this, arguments);

    if (!watched) return request;

    return request
      .then(function (response) {
        if (response && response.ok) clearState();
        else showState('error');
        return response;
      })
      .catch(function (error) {
        /* An aborted request is the visitor typing again, not a failure. */
        const aborted = error && (error.name === 'AbortError' || error.code === 20);
        if (!aborted) {
          showState(navigator.onLine === false ? 'offline' : 'error');
        }
        throw error;
      });
  };

  window.addEventListener('offline', function () {
    showState('offline');
  });

  window.addEventListener('online', clearState);
})();
